import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trip } from '@/types';
import * as tripService from '@/services/tripService';
import TripSection from '@/components/trip/TripSection';
import Header from '@/components/layout/Header';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/hooks/useI18n';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { ActionItem } from '@/components/layout/Header';
import { Box, Container, Typography } from '@mui/material';
import { groupTripsByDate } from '@/components/trip/utils';

const HomePage = () => {
    const [trips, setTrips] = useState<Trip[]>([]);
    const navigate = useNavigate();
    const { currentUser, isAdmin } = useAuth();
    const { t } = useI18n();

    useEffect(() => {
        // Nur nach Reisen mit offener Anmeldung lauschen
        const unsubscribe = tripService.listenToTrips((allTrips) => {
            setTrips(allTrips);
        }, [['isSignUpOpen', '==', true]]);

        return () => unsubscribe();
    }, []);

    const handleSelectTrip = (tripId: string) => {
        if (isAdmin) {
            // Admins gehen direkt zur Trip-Verwaltungsseite
            navigate(`/trip/${tripId}`);
        } else {
            // Normale Benutzer gehen zur Anmeldeseite
            navigate(`/trip/${tripId}/signup`);
        }
    };

    const headerActions: ActionItem[] = [];
    if (currentUser) {
        headerActions.push({
            label: t('navigation.dashboard'),
            icon: DashboardIcon,
            onClick: () => navigate('/dashboard'),
        });
    }

    // Gruppiere Reisen nach Datum
    const { current: currentTrips, past: pastTrips } = groupTripsByDate(trips);

    return (
        <Box sx={{ height: '100%' }}>
            <Header title={`🏔️ ${t('app.title')}`} actions={headerActions} />

            <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 } }}>
                <Box sx={{ textAlign: 'center', mb: 6 }}>
                    <Typography variant="h2" gutterBottom sx={{ color: 'grey.900' }}>
                        {t('pages.home.title')}
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
                        {t('pages.home.subtitle')}
                    </Typography>
                </Box>

                {/* Aktuelle Reisen Sektion */}
                <TripSection
                    title={t('trip.currentTrips')}
                    trips={currentTrips}
                    emptyMessage={t('trip.noCurrentTrips')}
                    onSelectTrip={handleSelectTrip}
                />

                {/* Vergangene Reisen Sektion */}
                <TripSection
                    title={t('trip.pastTrips')}
                    trips={pastTrips}
                    emptyMessage={t('trip.noPastTrips')}
                    onSelectTrip={handleSelectTrip}
                />
            </Container>
        </Box>
    );
};

export default HomePage;
